import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from "react-router-dom";

export const useChatbotBaseMutations = (id: string | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();

  const updateChatbot = useMutation({
    mutationFn: async (updates: any) => {
      if (!id) throw new Error("No chatbot ID provided");
      const { error } = await supabase
        .from("chatbots")
        .update(updates)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatbot", id] });
      toast({
        title: "Settings saved",
        description: "Your chatbot settings have been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const deleteChatbot = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No chatbot ID provided");
      
      const { error } = await supabase
        .from("chatbots")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatbots"] });
      toast({
        title: "Chatbot deleted",
        description: "The chatbot has been successfully deleted.",
      });
      navigate("/portal/chatbots");
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error deleting chatbot",
        description: error.message,
      });
    },
  });

  const uploadAvatar = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      if (!id) throw new Error("No chatbot ID provided");
      
      // Check if file is empty (for removal)
      if (file.size === 0) {
        const { error: updateError } = await supabase
          .from('chatbots')
          .update({ avatar_url: null })
          .eq('id', id);
        
        if (updateError) throw updateError;
        return null;
      }
      
      // Generate a unique filename to avoid collisions
      const fileExt = file.name.split('.').pop();
      const fileName = `${id}/${uuidv4()}.${fileExt}`;
      const filePath = fileName;
      
      try {
        // Check if the bucket exists, if not it will be created by Supabase automatically
        // Upload the file to the storage bucket
        const { error: uploadError } = await supabase
          .storage
          .from('chatbot-avatars')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: true
          });
        
        if (uploadError) throw uploadError;
        
        // Get the public URL of the uploaded file
        const { data } = supabase
          .storage
          .from('chatbot-avatars')
          .getPublicUrl(filePath);
        
        // Update the chatbot record with the avatar URL
        const { error: updateError } = await supabase
          .from('chatbots')
          .update({ avatar_url: data.publicUrl })
          .eq('id', id);
        
        if (updateError) throw updateError;
        
        return data.publicUrl;
      } catch (error) {
        console.error("Error in uploadAvatar:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatbot", id] });
      toast({
        title: "Avatar uploaded",
        description: "Your chatbot avatar has been updated successfully.",
      });
    },
    onError: (error: any) => {
      console.error("Error in uploadAvatar mutation:", error);
      toast({
        variant: "destructive",
        title: "Error uploading avatar",
        description: error.message,
      });
    },
  });

  const uploadOperatorAvatar = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      if (!id) throw new Error("No chatbot ID provided");
      
      // Check if file is empty (for removal)
      if (file.size === 0) {
        const { error: updateError } = await supabase
          .from('chatbots')
          .update({ operator_avatar_url: null })
          .eq('id', id);
        
        if (updateError) throw updateError;
        return null;
      }
      
      // Generate a unique filename to avoid collisions
      const fileExt = file.name.split('.').pop();
      const fileName = `${id}/operator-${uuidv4()}.${fileExt}`;
      const filePath = fileName;
      
      try {
        // Upload the file to the storage bucket
        const { error: uploadError } = await supabase
          .storage
          .from('chatbot-avatars')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: true
          });
        
        if (uploadError) {
          console.error("Upload error:", uploadError);
          throw uploadError;
        }
        
        // Get the public URL of the uploaded file
        const { data } = supabase
          .storage
          .from('chatbot-avatars')
          .getPublicUrl(filePath);
        
        if (!data.publicUrl) {
          throw new Error("Failed to get public URL for uploaded file");
        }
        
        console.log("Public URL:", data.publicUrl);
        
        // Update the chatbot record with the operator avatar URL
        const { error: updateError } = await supabase
          .from('chatbots')
          .update({ operator_avatar_url: data.publicUrl })
          .eq('id', id);
        
        if (updateError) {
          console.error("Update error:", updateError);
          throw updateError;
        }
        
        return data.publicUrl;
      } catch (error) {
        console.error("Error in uploadOperatorAvatar:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatbot", id] });
      toast({
        title: "Operator avatar uploaded",
        description: "The operator avatar has been updated successfully.",
      });
    },
    onError: (error: any) => {
      console.error("Error in uploadOperatorAvatar mutation:", error);
      toast({
        variant: "destructive",
        title: "Error uploading operator avatar",
        description: error.message,
      });
    },
  });

  return { updateChatbot, deleteChatbot, uploadAvatar, uploadOperatorAvatar };
};
